import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/timezone';
import { Inbox, AlertCircle, Settings, GitBranch, Loader2, RefreshCw, Clock } from 'lucide-react';
import { incidentAPI, serviceRequestAPI, changeRequestAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const stClass = {
  new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-purple-100 text-purple-800', resolved: 'bg-emerald-200 text-emerald-900',
  closed: 'bg-slate-200 text-slate-600', cancelled: 'bg-rose-100 text-rose-700',
};
const prClass = { p1: 'bg-rose-100 text-rose-800', p2: 'bg-amber-100 text-amber-800', p3: 'bg-blue-100 text-blue-800', p4: 'bg-slate-200 text-slate-700' };
const srUrgencyClass = { asap: 'bg-rose-100 text-rose-800', this_week: 'bg-amber-100 text-amber-800', standard: 'bg-blue-100 text-blue-800', no_rush: 'bg-slate-200 text-slate-700' };
const srUrgencyLabel = { asap: 'ASAP', this_week: 'This Week', standard: 'Standard', no_rush: 'No Rush' };
const crPriorityClass = { immediate: 'bg-rose-100 text-rose-800', high: 'bg-amber-100 text-amber-800', medium: 'bg-blue-100 text-blue-800', low: 'bg-slate-200 text-slate-700' };

function TicketPriority({ t }) {
  // Support _type (MyBucketPage internal) and ticket_type/type (API field)
  const type = t._type || t.ticket_type || t.type;
  if (type === 'incident') {
    const p = t.priority;
    if (!p) return <span className="text-slate-300">—</span>;
    return <span className={`${prClass[p] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{p.toUpperCase()}</span>;
  }
  if (type === 'sr' || t.urgency_level) {
    const u = t.urgency_level || 'standard';
    return <span className={`${srUrgencyClass[u] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{srUrgencyLabel[u] || u}</span>;
  }
  if (type === 'cr' || t.cr_priority || t.risk_level) {
    const p = t.cr_priority || t.risk_level || 'medium';
    return <span className={`${crPriorityClass[p] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>;
  }
  return <span className="text-slate-300">—</span>;
}

function SLABadge({ due, met }) {
  if (met === true) return <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">SLA Met</span>;
  if (met === false) return <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full">SLA Breached</span>;
  if (!due) return null;
  const diff = new Date(due) - new Date();
  const h = Math.round(diff / 3600000);
  if (diff < 0) return <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full">{Math.abs(h)}h overdue</span>;
  if (h < 4) return <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{h}h left</span>;
  return <span className="text-xs bg-slate-50 text-slate-500 px-2 py-0.5 rounded-full">{Math.round(h / 24)}d left</span>;
}

export default function MyBucketPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = { assigned_to: user.id, ordering: '-created_at', page_size: 100 };
      const [incRes, srRes, crRes] = await Promise.allSettled([
        incidentAPI.list(params),
        serviceRequestAPI.list(params),
        changeRequestAPI.list(params),
      ]);
      const all = [];
      if (incRes.status === 'fulfilled') (incRes.value.data?.results || incRes.value.data || []).forEach(t => all.push({ ...t, _type: 'incident' }));
      if (srRes.status === 'fulfilled') (srRes.value.data?.results || srRes.value.data || []).forEach(t => all.push({ ...t, _type: 'sr' }));
      if (crRes.status === 'fulfilled') (crRes.value.data?.results || crRes.value.data || []).forEach(t => all.push({ ...t, _type: 'cr' }));
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTickets(all);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]); // eslint-disable-line

  const openStatuses = ['new', 'assigned', 'in_progress', 'pending_user', 'pending_approval', 'reopened'];
  const byType = filter === 'all' ? tickets : tickets.filter(t => t._type === filter);
  const filtered = statusFilter === 'open' ? byType.filter(t => openStatuses.includes(t.status)) : statusFilter === 'closed' ? byType.filter(t => ['resolved', 'closed', 'cancelled'].includes(t.status)) : byType;

  const counts = {
    all: tickets.length,
    incident: tickets.filter(t => t._type === 'incident').length,
    sr: tickets.filter(t => t._type === 'sr').length,
    cr: tickets.filter(t => t._type === 'cr').length,
  };
  const openCount = tickets.filter(t => openStatuses.includes(t.status)).length;

  const typeIcon = { incident: AlertCircle, sr: Settings, cr: GitBranch };
  const typeColor = { incident: 'text-rose-600', sr: 'text-blue-600', cr: 'text-purple-600' };
  const typeTag = { incident: 'bg-rose-100 text-rose-800', sr: 'bg-blue-100 text-blue-800', cr: 'bg-purple-100 text-purple-800' };

  return (
    <div data-testid="my-bucket-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Inbox className="w-6 h-6 text-violet-500" /> My Bucket
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Tickets assigned to you — <span className="font-medium text-amber-600 dark:text-amber-400">{openCount} open</span>
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm bg-white dark:bg-slate-800 border dark:border-slate-700 hover:bg-slate-50 text-slate-600 dark:text-slate-300">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-3 mb-5">
        {/* Type filter */}
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
          {[['all','All'], ['incident','INC'], ['sr','SR'], ['cr','CR']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} data-testid={`type-${val}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === val ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
              {label} <span className="opacity-60">({counts[val]})</span>
            </button>
          ))}
        </div>
        {/* Status filter */}
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
          {[['open','Open'], ['closed','Resolved/Closed'], ['all','All']].map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === val ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <Inbox className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
          <p className="text-slate-500">{statusFilter === 'open' ? 'No open tickets in your bucket.' : 'No tickets found.'}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="my-bucket-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Ticket</th>
                <th className="px-4 py-3 text-left font-semibold">Title</th>
                <th className="px-4 py-3 text-center font-semibold">Type</th>
                <th className="px-4 py-3 text-center font-semibold">Priority</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">SLA</th>
                <th className="px-4 py-3 text-left font-semibold">Reporter</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {filtered.map(t => {
                const Icon = typeIcon[t._type] || AlertCircle;
                return (
                  <tr key={`${t._type}-${t.id}`} onClick={() => navigate(`/ticket/${t._type}/${t.id}`)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer" data-testid={`row-${t.ticket_number}`}>
                    <td className="px-4 py-3 font-mono font-bold text-sky-700 dark:text-sky-400">{t.ticket_number}</td>
                    <td className="px-4 py-3 dark:text-white max-w-xs truncate">{t.title}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${typeTag[t._type]}`}>
                        <Icon className={`w-3 h-3 ${typeColor[t._type]}`} />{t._type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <TicketPriority t={t} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`${stClass[t.status] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs`}>{t.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <SLABadge due={t.resolution_due} met={t.sla_resolution_met} />
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{t.reporter_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{t.created_at ? formatDate(t.created_at) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
